import { Box, Container, Typography, Paper, Chip, LinearProgress, Switch, Tooltip } from "@mui/material";
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import { vortxColors, vortxTypography, useThemeMode, EnergyFlowTopology3D } from '@vortx/shared-ui';
import {
    ElectricBolt,
    SolarPower,
    BatteryChargingFull,
    LocalShipping,
    TrendingUp,
    Warning,
    CheckCircle,
    Lock,
    Speed,
    AcUnit,
    DragIndicator,
    RestartAlt
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import TopologyDiagram from '../components/common/TopologyDiagram';

const ResponsiveGridLayout = WidthProvider(Responsive);

// Colors
const liveDataColor = '#3f6a64';
const deepCharcoal = '#1a1a1a';
const slateBackground = '#242424';

// Animated styles


// Fleet and charger data with telematics
const fleetData = [
    { vin: 'TRK-001', soc: 85, status: 'Charging', charger: 'EVSE-01', arrival: '6:30 AM', departure: '2:00 PM', driver: 'J. Smith' },
    { vin: 'TRK-002', soc: 62, status: 'Managed', charger: 'EVSE-02', arrival: '7:15 AM', departure: '3:30 PM', driver: 'M. Chen' },
    { vin: 'TRK-003', soc: 94, status: 'Ready', charger: 'EVSE-03', arrival: '5:45 AM', departure: '1:00 PM', driver: 'R. Patel' },
    { vin: 'TRK-004', soc: 45, status: 'Charging', charger: 'EVSE-04', arrival: '8:00 AM', departure: '4:30 PM', driver: 'A. Lopez' },
    { vin: 'TRK-005', soc: 78, status: 'Managed', charger: 'EVSE-05', arrival: '6:00 AM', departure: '2:30 PM', driver: 'T. Kim' },
    { vin: 'TRK-006', soc: 100, status: 'Ready', charger: 'EVSE-06', arrival: '5:30 AM', departure: '12:00 PM', driver: 'L. Brown' },
];

// Default layout configuration
const getDefaultLayouts = () => ({
    lg: [
        { i: 'topology', x: 0, y: 0, w: 8, h: 6, minW: 6, minH: 5 },
        { i: 'solar', x: 8, y: 0, w: 2, h: 2, minW: 2, minH: 2 },
        { i: 'battery', x: 10, y: 0, w: 2, h: 2, minW: 2, minH: 2 },
        { i: 'grid', x: 8, y: 2, w: 2, h: 2, minW: 2, minH: 2 },
        { i: 'hvac', x: 10, y: 2, w: 2, h: 4, minW: 2, minH: 4 },
        { i: 'sitedna', x: 0, y: 5, w: 4, h: 4, minW: 2, minH: 3 },
        { i: 'interconnection', x: 5, y: 5, w: 3, h: 3, minW: 2, minH: 2 },
        { i: 'events', x: 8, y: 4, w: 2, h: 4, minW: 2, minH: 2 },
        { i: 'fleet', x: 0, y: 8, w: 8, h: 3, minW: 4, minH: 3 },
    ],
    md: [
        { i: 'topology', x: 0, y: 0, w: 8, h: 5 },
        { i: 'solar', x: 8, y: 0, w: 2, h: 2 },
        { i: 'battery', x: 8, y: 2, w: 2, h: 2 },
        { i: 'grid', x: 0, y: 5, w: 2, h: 2 },
        { i: 'hvac', x: 2, y: 5, w: 3, h: 4 },
        { i: 'sitedna', x: 4, y: 5, w: 4, h: 3 },
        { i: 'interconnection', x: 0, y: 7, w: 2, h: 3 },
        { i: 'events', x: 8, y: 4, w: 2, h: 4 },
        { i: 'fleet', x: 0, y: 10, w: 10, h: 2 },
    ],
    sm: [
        { i: 'topology', x: 0, y: 0, w: 6, h: 4 },
        { i: 'solar', x: 0, y: 4, w: 2, h: 2 },
        { i: 'battery', x: 2, y: 4, w: 2, h: 2 },
        { i: 'grid', x: 4, y: 4, w: 2, h: 2 },
        { i: 'hvac', x: 0, y: 6, w: 3, h: 4 },
        { i: 'sitedna', x: 3, y: 6, w: 3, h: 3 },
        { i: 'interconnection', x: 0, y: 9, w: 3, h: 3 },
        { i: 'events', x: 3, y: 9, w: 3, h: 3 },
        { i: 'fleet', x: 0, y: 12, w: 6, h: 2 },
    ],
});

// Tile wrapper with drag handle - theme-aware with responsive title
const Tile = ({ title, icon, children, noPadding = false, bgCard, bgHeader, textColor }) => (
    <Paper sx={{
        bgcolor: bgCard || '#FFFFFF',
        border: `1px solid ${vortxColors.gold}`,
        borderRadius: 1.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    }}>
        <Box className="drag-handle" sx={{
            display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
            bgcolor: bgHeader || '#f0f0f0', borderBottom: `1px solid ${vortxColors.gold}30`,
            cursor: 'grab', '&:active': { cursor: 'grabbing' },
            minWidth: 0 // Enable text truncation in flex children
        }}>
            <DragIndicator sx={{ color: vortxColors.gold, fontSize: 16, mr: 0.5, opacity: 0.6, flexShrink: 0 }} />
            <Box sx={{ flexShrink: 0 }}>{icon}</Box>
            <Tooltip title={title} placement="top" arrow>
                <Typography sx={{
                    fontFamily: vortxTypography.heading, color: textColor || vortxColors.charcoal, fontWeight: 600, fontSize: '0.75rem', ml: 0.5,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0
                }}>
                    {title}
                </Typography>
            </Tooltip>
        </Box>
        <Box sx={{ flex: 1, p: noPadding ? 0 : 1.5, overflow: 'auto' }}>
            {children}
        </Box>
    </Paper>
);

// Metric card content - theme-aware
const MetricContent = ({ value, unit, subtitle, color = liveDataColor, textColor, children }) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Typography variant="h4" sx={{ fontFamily: vortxTypography.heading, color, fontWeight: 700, lineHeight: 1.1 }}>
            {value}<Typography component="span" sx={{ fontSize: '0.8rem', color: textColor || vortxColors.stone, ml: 0.5 }}>{unit}</Typography>
        </Typography>
        {subtitle && <Typography sx={{ color: textColor || vortxColors.stone, fontSize: '0.65rem', mt: 0.5 }}>{subtitle}</Typography>}
        {children}
    </Box>
);

// HVAC Predictive Analytics - Expanded
const HVACContent = ({ isDark, textSecondary }) => {
    const cardBg = isDark ? deepCharcoal : '#f5f5f5';
    const textColor = textSecondary || vortxColors.stone;

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Load Prediction Chart */}
            <Box sx={{ flex: 1, minHeight: 40 }}>
                <svg viewBox="0 0 200 40" style={{ width: '100%', height: '100%' }} preserveAspectRatio="xMidYMid meet">
                    {/* Historical line */}
                    <polyline points="0,35 20,32 40,28 60,30 80,25 100,22" fill="none" stroke={vortxColors.gold} strokeWidth="1.5" />
                    {/* Prediction line (dashed) */}
                    <polyline points="100,22 120,18 140,15 160,12 180,10 200,8" fill="none" stroke={vortxColors.gold} strokeWidth="1.5" strokeDasharray="4 2" />
                    {/* Now marker */}
                    <circle cx="100" cy="22" r="3" fill={vortxColors.gold} />
                    <text x="100" y="38" textAnchor="middle" fill={textColor} fontSize="5" fontFamily="Inter">now</text>
                </svg>
            </Box>

            {/* Current & Predicted Load */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box>
                    <Typography sx={{ color: vortxColors.gold, fontWeight: 700, fontSize: '1.1rem', fontFamily: vortxTypography.heading }}>
                        145 kW
                    </Typography>
                    <Typography sx={{ color: textColor, fontSize: '0.5rem' }}>current load</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ color: liveDataColor, fontWeight: 600, fontSize: '0.9rem', fontFamily: vortxTypography.heading }}>
                        → 120 kW
                    </Typography>
                    <Typography sx={{ color: textColor, fontSize: '0.5rem' }}>+4h predicted</Typography>
                </Box>
            </Box>

            {/* Set Points & Zones */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ flex: 1, p: 0.75, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}20` }}>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem', textTransform: 'uppercase' }}>Set Point</Typography>
                    <Typography sx={{ color: vortxColors.gold, fontWeight: 700, fontSize: '0.85rem' }}>72°F</Typography>
                </Box>
                <Box sx={{ flex: 1, p: 0.75, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}20` }}>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem', textTransform: 'uppercase' }}>Deadband</Typography>
                    <Typography sx={{ color: textColor, fontWeight: 600, fontSize: '0.85rem' }}>±2°F</Typography>
                </Box>
                <Box sx={{ flex: 1, p: 0.75, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}20` }}>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem', textTransform: 'uppercase' }}>Zones</Typography>
                    <Typography sx={{ color: '#4CAF50', fontWeight: 600, fontSize: '0.85rem' }}>4/4 ✓</Typography>
                </Box>
            </Box>

            {/* Pre-cooling Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5, bgcolor: 'rgba(76,175,80,0.1)', borderRadius: 0.5, border: '1px solid rgba(76,175,80,0.3)' }}>
                <Typography sx={{ color: '#4CAF50', fontSize: '0.55rem', fontWeight: 600 }}>● Pre-cooling active</Typography>
                <Typography sx={{ color: textColor, fontSize: '0.5rem' }}>Peak @ 2:00 PM</Typography>
            </Box>
        </Box>
    );
};

// Site DNA content - expanded with more AI details
const SiteDNAContent = ({ isDark, textSecondary }) => {
    const cardBg = isDark ? deepCharcoal : '#f5f5f5';
    const textColor = textSecondary || vortxColors.stone;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
            {/* Top row - MPC & Forecast */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ flex: 1, p: 1, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}30` }}>
                    <Typography sx={{ color: textColor, textTransform: 'uppercase', fontSize: '0.45rem' }}>MPC Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                        <CheckCircle sx={{ color: '#4CAF50', fontSize: 12, mr: 0.3 }} />
                        <Typography sx={{ color: '#4CAF50', fontWeight: 600, fontSize: '0.7rem' }}>ACTIVE</Typography>
                    </Box>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem', mt: 0.25 }}>Next cycle: 4h 15m</Typography>
                </Box>
                <Box sx={{ flex: 1, p: 1, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}30` }}>
                    <Typography sx={{ color: textColor, textTransform: 'uppercase', fontSize: '0.45rem' }}>Forecast</Typography>
                    <Typography sx={{ color: liveDataColor, fontFamily: vortxTypography.heading, fontWeight: 700, fontSize: '1rem' }}>94.7%</Typography>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem' }}>7-day accuracy</Typography>
                </Box>
            </Box>

            {/* Middle row - Savings & Efficiency */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Box sx={{ flex: 1, p: 1, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}30` }}>
                    <Typography sx={{ color: textColor, textTransform: 'uppercase', fontSize: '0.45rem' }}>MTD Savings</Typography>
                    <Typography sx={{ color: vortxColors.gold, fontFamily: vortxTypography.heading, fontWeight: 700, fontSize: '0.9rem' }}>$4,280</Typography>
                    <Typography sx={{ color: '#4CAF50', fontSize: '0.45rem' }}>↑ 12% vs target</Typography>
                </Box>
                <Box sx={{ flex: 1, p: 1, bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}30` }}>
                    <Typography sx={{ color: textColor, textTransform: 'uppercase', fontSize: '0.45rem' }}>Peak Shaved</Typography>
                    <Typography sx={{ color: vortxColors.gold, fontFamily: vortxTypography.heading, fontWeight: 700, fontSize: '0.9rem' }}>234 kW</Typography>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem' }}>Today's max</Typography>
                </Box>
            </Box>

            {/* Bottom - Learning Status */}
            <Box sx={{ p: 0.75, bgcolor: 'rgba(178, 140, 80, 0.1)', borderRadius: 0.5, border: `1px solid ${vortxColors.gold}30` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{ color: vortxColors.gold, fontSize: '0.5rem', fontWeight: 600 }}>● AI Learning</Typography>
                    <Typography sx={{ color: textColor, fontSize: '0.45rem' }}>1,247 patterns</Typography>
                </Box>
                <LinearProgress variant="determinate" value={78} sx={{ height: 3, borderRadius: 1, mt: 0.5, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)', '& .MuiLinearProgress-bar': { bgcolor: vortxColors.gold } }} />
            </Box>
        </Box>
    );
};

// Interconnection content - theme-aware
const InterconnectionContent = ({ nonExportMode, setNonExportMode, isDark, textSecondary }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
        <Box sx={{
            p: 1, bgcolor: nonExportMode ? 'rgba(76,175,80,0.12)' : 'transparent',
            border: `1px solid ${nonExportMode ? '#4CAF50' : vortxColors.gold}`, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
            <Box>
                <Typography sx={{ color: nonExportMode ? '#4CAF50' : (textSecondary || vortxColors.stone), fontWeight: 600, fontSize: '0.7rem' }}>Non-Export</Typography>
                <Typography sx={{ color: textSecondary || vortxColors.stone, fontSize: '0.5rem' }}>Hardware Enforced</Typography>
            </Box>
            <Switch checked={nonExportMode} onChange={(e) => setNonExportMode(e.target.checked)} size="small"
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4CAF50' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4CAF50' } }} />
        </Box>
        <Tooltip title="Contact Vortx to enable">
            <Box sx={{ p: 1, border: `1px solid ${vortxColors.gold}30`, borderRadius: 1, opacity: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Typography sx={{ color: vortxColors.gold, fontWeight: 600, fontSize: '0.6rem' }}>UL 3141</Typography>
                        <Lock sx={{ color: vortxColors.gold, fontSize: 10 }} />
                    </Box>
                    <Typography sx={{ color: textSecondary || vortxColors.stone, fontSize: '0.45rem' }}>Bidirectional</Typography>
                </Box>
                <Switch disabled size="small" />
            </Box>
        </Tooltip>
    </Box>
);

// Events content - theme-aware
const EventsContent = ({ textSecondary }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {[
            { icon: <CheckCircle sx={{ color: '#4CAF50', fontSize: 12 }} />, text: 'MPC cycle', time: '2m' },
            { icon: <TrendingUp sx={{ color: liveDataColor, fontSize: 12 }} />, text: 'Peak shaving', time: '15m' },
            { icon: <Warning sx={{ color: vortxColors.gold, fontSize: 12 }} />, text: 'Low SoC TRK-04', time: '28m' },
        ].map((e, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {e.icon}
                <Typography sx={{ color: textSecondary || vortxColors.stone, flex: 1, fontSize: '0.6rem' }}>{e.text}</Typography>
                <Typography sx={{ color: textSecondary || vortxColors.stone, fontSize: '0.5rem' }}>{e.time}</Typography>
            </Box>
        ))}
    </Box>
);

// Fleet content - enhanced with telematics
const FleetContent = ({ isDark, textSecondary }) => {
    const cardBg = isDark ? deepCharcoal : '#f5f5f5';
    const textColor = textSecondary || vortxColors.stone;
    const getStatusColor = (s) => s === 'Charging' ? liveDataColor : s === 'Ready' ? '#4CAF50' : vortxColors.gold;

    return (
        <Box sx={{ display: 'flex', height: '100%', overflowX: 'auto', gap: 0.5, p: 0.5 }}>
            {fleetData.map((t, i) => (
                <Box key={i} sx={{
                    display: 'flex', flexDirection: 'column', p: 1,
                    bgcolor: cardBg, borderRadius: 1, border: `1px solid ${vortxColors.gold}20`,
                    minWidth: 120
                }}>
                    {/* Vehicle Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ color: isDark ? vortxColors.white : vortxColors.charcoal, fontFamily: vortxTypography.mono, fontSize: '0.6rem', fontWeight: 600 }}>
                            {t.vin}
                        </Typography>
                        <Chip label={t.status} size="small" sx={{
                            bgcolor: `${getStatusColor(t.status)}15`, color: getStatusColor(t.status),
                            fontSize: '0.4rem', height: 14, border: `1px solid ${getStatusColor(t.status)}30`
                        }} />
                    </Box>

                    {/* SoC Bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <LinearProgress variant="determinate" value={t.soc} sx={{
                            flex: 1, height: 6, borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                            '& .MuiLinearProgress-bar': { bgcolor: t.soc > 80 ? '#4CAF50' : t.soc > 40 ? vortxColors.gold : '#f44336' }
                        }} />
                        <Typography sx={{ color: textColor, fontSize: '0.5rem', minWidth: 22 }}>{t.soc}%</Typography>
                    </Box>

                    {/* Telematics */}
                    <Box sx={{ display: 'flex', gap: 0.5, fontSize: '0.4rem' }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: textColor, fontSize: '0.4rem', textTransform: 'uppercase' }}>Arr</Typography>
                            <Typography sx={{ color: vortxColors.gold, fontSize: '0.5rem', fontWeight: 600 }}>{t.arrival}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ color: textColor, fontSize: '0.4rem', textTransform: 'uppercase' }}>Dep</Typography>
                            <Typography sx={{ color: liveDataColor, fontSize: '0.5rem', fontWeight: 600 }}>{t.departure}</Typography>
                        </Box>
                    </Box>

                    {/* Driver */}
                    <Typography sx={{ color: textColor, fontSize: '0.45rem', mt: 0.5, borderTop: `1px solid ${vortxColors.gold}15`, pt: 0.5 }}>
                        {t.driver}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

export default function Flux() {
    const { isDark } = useThemeMode();
    const [nonExportMode, setNonExportMode] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [layouts, setLayouts] = useState(() => {
        const saved = localStorage.getItem('flux-dashboard-layout');
        return saved ? JSON.parse(saved) : getDefaultLayouts();
    });

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const handleLayoutChange = (currentLayout, allLayouts) => {
        setLayouts(allLayouts);
        localStorage.setItem('flux-dashboard-layout', JSON.stringify(allLayouts));
    };

    const resetLayout = () => {
        const defaults = getDefaultLayouts();
        setLayouts(defaults);
        localStorage.setItem('flux-dashboard-layout', JSON.stringify(defaults));
    };

    // Theme-aware colors
    const bgMain = isDark ? deepCharcoal : vortxColors.white;
    const bgCard = isDark ? slateBackground : '#FFFFFF';
    const bgHeader = isDark ? deepCharcoal : '#f0f0f0';
    const textPrimary = isDark ? vortxColors.white : vortxColors.charcoal;
    const textSecondary = isDark ? vortxColors.stone : '#666666';

    const GridStyles = () => (
        <style>{`
            .react-grid-item > .react-resizable-handle::after {
                border-color: ${vortxColors.gold} !important;
            }
            .react-grid-placeholder {
                background: ${vortxColors.gold} !important;
                opacity: 0.2 !important;
                border-radius: 8px !important;
            }
            .react-grid-item.react-draggable-dragging {
                box-shadow: 0 8px 32px rgba(178, 140, 80, 0.4) !important;
                z-index: 100 !important;
            }
        `}</style>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: bgMain, py: 2 }}>
            <GridStyles />
            <Container maxWidth="xl">
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 1.5, borderBottom: `1px solid ${vortxColors.gold}` }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontFamily: vortxTypography.heading, color: vortxColors.gold, fontWeight: 800, letterSpacing: '0.05em' }}>
                            FLUX EMS
                        </Typography>
                        <Typography variant="caption" sx={{ color: vortxColors.stone }}>
                            Vortx Logistics Depot — Sacramento, CA • Digital Twin
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Tooltip title="Reset layout to default">
                            <Box onClick={resetLayout} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: vortxColors.stone, '&:hover': { color: vortxColors.gold } }}>
                                <RestartAlt sx={{ fontSize: 16 }} />
                                <Typography sx={{ fontSize: '0.7rem' }}>Reset</Typography>
                            </Box>
                        </Tooltip>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontFamily: vortxTypography.mono, color: liveDataColor, fontWeight: 600, fontSize: '1rem' }}>
                                {currentTime.toLocaleTimeString()}
                            </Typography>
                            <Typography variant="caption" sx={{ color: vortxColors.stone, fontSize: '0.6rem' }}>
                                {currentTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Draggable Grid */}
                <ResponsiveGridLayout
                    layouts={layouts}
                    breakpoints={{ lg: 1200, md: 996, sm: 768 }}
                    cols={{ lg: 12, md: 10, sm: 6 }}
                    rowHeight={60}
                    margin={[12, 12]}
                    containerPadding={[0, 0]}
                    onLayoutChange={handleLayoutChange}
                    draggableHandle=".drag-handle"
                    compactType="vertical"
                    preventCollision={false}
                >
                    <div key="topology">
                        <Tile title="Site Digital Twin — 700VDC Microgrid" icon={<Speed sx={{ color: liveDataColor, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <EnergyFlowTopology3D
                                solarKw={250}
                                batteryKw={195}
                                evKw={300}
                                loadKw={150}
                                gridKw={0}
                                isDark={isDark}
                                height={400}
                                FallbackComponent={TopologyDiagram}
                            />
                        </Tile>
                    </div>
                    <div key="solar">
                        <Tile title="Solar" icon={<SolarPower sx={{ color: vortxColors.gold, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <MetricContent value="250" unit="kW" subtitle="Today: 1,784 kWh" color={vortxColors.gold} textColor={textSecondary} />
                        </Tile>
                    </div>
                    <div key="battery">
                        <Tile title="Battery" icon={<BatteryChargingFull sx={{ color: liveDataColor, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <MetricContent value="78" unit="%" subtitle="Discharging" textColor={textSecondary}>
                                <LinearProgress variant="determinate" value={78} sx={{
                                    mt: 1, height: 6, borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    '& .MuiLinearProgress-bar': { bgcolor: liveDataColor }
                                }} />
                            </MetricContent>
                        </Tile>
                    </div>
                    <div key="grid">
                        <Tile title="Grid" icon={<ElectricBolt sx={{ color: '#4CAF50', fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <MetricContent value="0.0" unit="kW" subtitle="Non-Export Active" color="#4CAF50" textColor={textSecondary} />
                        </Tile>
                    </div>
                    <div key="hvac">
                        <Tile title="HVAC Loads (Via BMS)" icon={<AcUnit sx={{ color: vortxColors.gold, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <HVACContent isDark={isDark} textSecondary={textSecondary} />
                        </Tile>
                    </div>
                    <div key="sitedna">
                        <Tile title="AI Site DNA" icon={<Speed sx={{ color: liveDataColor, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <SiteDNAContent isDark={isDark} textSecondary={textSecondary} />
                        </Tile>
                    </div>
                    <div key="interconnection">
                        <Tile title="Interconnection" icon={<ElectricBolt sx={{ color: vortxColors.gold, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <InterconnectionContent nonExportMode={nonExportMode} setNonExportMode={setNonExportMode} isDark={isDark} textSecondary={textSecondary} />
                        </Tile>
                    </div>
                    <div key="events">
                        <Tile title="Events" icon={<Warning sx={{ color: vortxColors.gold, fontSize: 14 }} />} bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <EventsContent textSecondary={textSecondary} />
                        </Tile>
                    </div>
                    <div key="fleet">
                        <Tile title="Fleet Operations (Via CMS & Telematics)" icon={<LocalShipping sx={{ color: vortxColors.gold, fontSize: 14 }} />} noPadding bgCard={bgCard} bgHeader={bgHeader} textColor={textPrimary}>
                            <FleetContent isDark={isDark} textSecondary={textSecondary} />
                        </Tile>
                    </div>
                </ResponsiveGridLayout>
            </Container>
        </Box>
    );
}
